import React from 'react';
import Page from './page';

function PeticlePolicy() {
    return (
        <Page label="Peticle — Legal" title="Privacy Policy">
            <div className="doc">
                Effective Date: 17 May 2025
                <br /><br />
                Welcome to Peticle, a pet care app created by Unit 326. This Privacy Policy explains how we collect,
                use, and protect your data — and your pet's data — when you use our app. We are committed to
                safeguarding your privacy and providing a safe, reliable experience for you and your pets.
                <br /><br />

                <h3>Interpretation and Definitions</h3><br />

                <h5>Interpretation</h5>
                <p>The words of which the initial letter is capitalized have meanings defined under the following
                    conditions. The following definitions shall have the same meaning regardless of whether they appear
                    in singular or in plural.</p>

                <h6>Definitions</h6>
                <p>For the purposes of this Privacy Policy:</p>
                <ul>
                    <li>
                        <p><strong>Account</strong> means a unique account created for You to access our Service or
                            parts of our Service.</p>
                    </li>
                    <li>
                        <p><strong>Application</strong> refers to Peticle, the software program provided by the
                            Company.</p>
                    </li>
                    <li>
                        <p><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this
                            Agreement) refers to Unit 326, Joburg, South Africa.</p>
                    </li>
                    <li>
                        <p><strong>Country</strong> refers to: South Africa</p>
                    </li>
                    <li>
                        <p><strong>Device</strong> means any device that can access the Service such as a computer, a
                            cellphone or a digital tablet.</p>
                    </li>
                    <li>
                        <p><strong>Pet Data</strong> means any images, health information, breed data, or other
                            information relating to Your pet that You upload or that is generated through use of the
                            Service.</p>
                    </li>
                    <li>
                        <p><strong>Personal Data</strong> is any information that relates to an identified or
                            identifiable individual.</p>
                    </li>
                    <li>
                        <p><strong>Service</strong> refers to the Application.</p>
                    </li>
                    <li>
                        <p><strong>Service Provider</strong> means any natural or legal person who processes the data on
                            behalf of the Company, including third-party AI or computer vision providers used to power
                            the app's scan and analysis features.</p>
                    </li>
                    <li>
                        <p><strong>Usage Data</strong> refers to data collected automatically, either generated by the
                            use of the Service or from the Service infrastructure itself.</p>
                    </li>
                    <li>
                        <p><strong>You</strong> means the individual accessing or using the Service, or the company, or
                            other legal entity on behalf of which such individual is accessing or using the Service, as
                            applicable.</p>
                    </li>
                </ul>

                <br /><br />

                <h3>1. Data We Collect</h3><br />

                <h5>Personal Data</h5>
                <p>While using Our Service, We may ask You to provide Us with certain personally identifiable
                    information. This may include, but is not limited to:</p>
                <ul>
                    <li>Email address</li>
                    <li>First name and last name</li>
                    <li>Profile information</li>
                    <li>Usage Data</li>
                </ul>

                <br />

                <h5>Pet Data</h5>
                <p>Peticle collects information about Your pets in order to provide its core features. This may include:</p>
                <ul>
                    <li>Pet name, species, breed, age, and weight</li>
                    <li>Photos and scans of Your pet (including images of wounds, coat, eyes, ears, and other body
                        areas) submitted for medical analysis</li>
                    <li>Breed identification results generated from scans</li>
                    <li>Health notes, observations, and vet visit records You enter manually</li>
                    <li>Reminder schedules for vet appointments, deworming, vaccinations, flea treatment, and other
                        care events</li>
                </ul>

                <br />

                <h5>Camera and Image Data</h5>
                <p>Peticle requires access to Your device's camera and photo library to enable pet scanning and medical
                    analysis features. Images submitted for scanning are processed to generate breed identification and
                    health assessments. We do not use these images for any purpose other than providing the requested
                    analysis results and improving the accuracy of our models.</p>
                <p>You can grant or revoke camera and photo library permissions at any time through Your device
                    settings. Revoking access will disable scanning features.</p>

                <br />

                <h5>Notification Access</h5>
                <p>Peticle requests permission to send You push notifications to deliver care reminders (e.g., vet
                    appointments, deworming schedules, medication reminders). You can manage notification preferences
                    at any time through Your device settings.</p>

                <br />

                <h5>Usage Data</h5>
                <p>Usage Data is collected automatically when using the Service. This may include Your device's IP
                    address, browser type, the pages of our Service that You visit, the time and date of Your visit,
                    time spent on those pages, unique device identifiers, and other diagnostic data.</p>

                <br /><br />

                <h3>2. How We Use Your Data</h3><br />
                <p>The Company may use Personal Data and Pet Data for the following purposes:</p>

                <ul>
                    <li>
                        <p><strong>To provide and maintain our Service</strong>, including breed identification, medical
                            image analysis, and care reminder features.</p>
                    </li>
                    <li>
                        <p><strong>To manage Your Account</strong> and provide You access to all Service
                            functionalities.</p>
                    </li>
                    <li>
                        <p><strong>To deliver care reminders</strong> for vet appointments, deworming, vaccinations,
                            and other pet health events You have scheduled.</p>
                    </li>
                    <li>
                        <p><strong>To improve our AI models</strong>: with Your consent, anonymized pet images and
                            scan results may be used to improve the accuracy of our breed identification and medical
                            analysis features.</p>
                    </li>
                    <li>
                        <p><strong>To contact You</strong> regarding updates, security notices, or important changes
                            to the Service.</p>
                    </li>
                    <li>
                        <p><strong>To manage Your requests</strong> and provide customer support.</p>
                    </li>
                    <li>
                        <p><strong>For analytics and service improvement</strong>: to identify usage trends and
                            evaluate and improve the Service.</p>
                    </li>
                </ul>

                <br />

                <h3>3. Medical Analysis Disclaimer</h3><br />
                <p>The medical analysis features in Peticle — including wound assessment, coat condition analysis, eye
                    and ear health checks, and other visual health evaluations — are powered by AI and computer vision
                    technology. These features are provided for <strong>informational purposes only</strong> and do
                    not constitute professional veterinary advice, diagnosis, or treatment.</p>
                <p>Always consult a qualified veterinarian for any concerns about Your pet's health. Do not delay
                    seeking professional veterinary care based on results provided by the app.</p>

                <br />

                <h3>4. Data Sharing</h3><br />
                <p>We may share Your information in the following situations:</p>
                <ul>
                    <li><strong>With Service Providers:</strong> We may share data with third-party providers who
                        assist in operating the Service, including cloud storage and AI processing providers. These
                        providers are contractually obligated to handle Your data securely and only for the purposes
                        of providing services to Us.
                    </li>
                    <li><strong>For business transfers:</strong> We may share or transfer Your data in connection with
                        a merger, acquisition, or sale of Company assets.
                    </li>
                    <li><strong>With Affiliates:</strong> We may share Your information with Our affiliates, who are
                        required to honor this Privacy Policy.
                    </li>
                    <li><strong>With Your consent:</strong> We may disclose Your information for any other purpose
                        with Your explicit consent.
                    </li>
                </ul>
                <p>We <strong>do not sell</strong> Your personal data or Pet Data to third parties.</p>

                <br />

                <h3>5. Data Security and Storage</h3><br />
                <p>We employ industry-standard security measures to protect Your data, including:</p>
                <ul>
                    <li>Encryption of data in transit and at rest</li>
                    <li>Restricted access to Your data, limited to authorized personnel only</li>
                    <li>Compliance with applicable data protection regulations, including POPIA (South Africa),
                        GDPR, and CCPA</li>
                </ul>
                <p>Your data is stored securely on our servers. While we take all reasonable steps to protect Your
                    data, no method of transmission or storage is 100% secure.</p>

                <br />

                <h3>6. Data Retention</h3><br />
                <p>We retain Your Personal Data and Pet Data only for as long as is necessary to provide the Service
                    and fulfill the purposes set out in this Privacy Policy. When Your account is deleted, We will
                    remove Your data in accordance with our data deletion procedures, except where We are required to
                    retain it by law.</p>

                <br />

                <h3>7. Your Privacy Rights</h3><br />
                <p>You have the right to:</p>
                <ul>
                    <li>Access the personal data We hold about You</li>
                    <li>Request correction of inaccurate data</li>
                    <li>Request deletion of Your data</li>
                    <li>Withdraw consent for AI model training at any time</li>
                    <li>Manage camera, photo, and notification permissions through Your device settings</li>
                </ul>
                <p>To exercise these rights, visit the account settings section of the app or contact Us at <a
                    href="mailto:hello@unit326.com">hello@unit326.com</a>. You may also visit our data settings
                    page <a href="https://unit326.com/data-settings">here</a>.</p>

                <br />

                <h3>8. Third-Party Services</h3><br />
                <p>Peticle may allow You to sign in using Gmail or Apple account credentials. Please refer to their
                    respective privacy policies for information on how they handle Your data. We may also integrate
                    with third-party AI or computer vision providers to power scanning features; these providers are
                    bound by data processing agreements with Us.</p>

                <br />

                <h3>9. Children's Privacy</h3><br />
                <p>Peticle is not intended for individuals under the age of 13. We do not knowingly collect personal
                    information from children. If You believe a child has provided Us with their personal information,
                    please contact Us at <a href="mailto:hello@unit326.com">hello@unit326.com</a> to have it
                    removed.</p>

                <br />

                <h3>10. Changes to This Privacy Policy</h3><br />
                <p>We reserve the right to modify this policy at any time. Changes will be effective upon posting
                    within the app or on our website. We encourage You to review this policy periodically. Continued
                    use of the Service after changes are posted constitutes Your acceptance of the revised policy.</p>

                <br />

                <h3>11. Contact Us</h3><br />
                <p>If you have any questions or concerns about this Privacy Policy, please contact us at:</p>
                <p>
                    <strong>Unit 326</strong><br />
                    Email: <a href="mailto:hello@unit326.com">hello@unit326.com</a><br />
                    Website: <a href="https://unit326.com/data-settings">unit326.com</a>
                </p>

                <br />
                Thank you for using Peticle and trusting us with the care of your pets.
                <br /><br />
                Unit 326
            </div>
        </Page>
    );
}

export default PeticlePolicy;
